import React, {
  createContext,
  useCallback,
  useLayoutEffect,
  useReducer,
  useRef,
} from 'react';

import type { CustomizationAction } from './actions';
import { customizationReducer } from './customizationReducer';
import {
  createInitialState,
  type CustomizationState,
  type TemplateIdentity,
} from './customizationState';

export const CustomizationStateContext =
  createContext<CustomizationState | null>(null);

export const CustomizationDispatchContext =
  createContext<React.Dispatch<CustomizationAction> | null>(null);

export const LatestCustomizationStateContext = createContext<
  (() => CustomizationState) | null
>(null);

type Props = {
  identity: TemplateIdentity;
  children: React.ReactNode;
};

export function TemplateCustomizationProvider({ identity, children }: Props) {
  const [state, dispatchToReducer] = useReducer(
    customizationReducer,
    identity,
    createInitialState
  );
  const latestStateRef = useRef(state);

  // A layout effect, so no dispatch can run between the commit and this sync
  useLayoutEffect(() => {
    latestStateRef.current = state;
  }, [state]);

  // React renders after an async callback returns. The ref advances here so the
  // next queued write reads the version its predecessor just confirmed
  const dispatch = useCallback((action: CustomizationAction) => {
    latestStateRef.current = customizationReducer(
      latestStateRef.current,
      action
    );
    dispatchToReducer(action);
  }, []);

  const getLatestState = useCallback(() => latestStateRef.current, []);

  const hasIdentityChanged =
    state.templateId !== identity.templateId ||
    state.channelId !== identity.channelId;

  if (hasIdentityChanged) {
    dispatchToReducer({ type: 'IDENTITY_CHANGED', identity });
  }

  return (
    <CustomizationDispatchContext.Provider value={dispatch}>
      <LatestCustomizationStateContext.Provider value={getLatestState}>
        <CustomizationStateContext.Provider value={state}>
          {children}
        </CustomizationStateContext.Provider>
      </LatestCustomizationStateContext.Provider>
    </CustomizationDispatchContext.Provider>
  );
}
